import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import { revertSessionSparx } from "../session/sessionSlice";
import type { RootState } from "@/store/app/storeTypes";
import {
  sparxsEditorConflictResolvedWithServerBlueprint,
  sparxsEditorRevertedSessionFileToOriginal,
  sparxsEditorSaveSucceededForFiles,
} from "@/features/sparxsEditor/store/domainActions";

export type ConflictReason = "newer_on_server" | "modified_on_server";

export interface ConflictEntry {
  fileName: string;
  reason: ConflictReason;
}

const initialState: ConflictEntry[] = [];

const filterOutConflictsWithFileNames = (
  state: ConflictEntry[],
  fileNames: Iterable<string>,
): ConflictEntry[] => {
  const remove = new Set(fileNames);
  return state.filter((c) => !remove.has(c.fileName));
};

const conflictSlice = createSlice({
  name: "conflict",
  initialState,
  reducers: {
    setConflicts: (_, action: PayloadAction<ConflictEntry[]>) => action.payload,
    clearConflicts: () => [],
    removeConflictFileNames: (state, action: PayloadAction<string[]>) => {
      return filterOutConflictsWithFileNames(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertSessionSparx, (state, action) =>
        filterOutConflictsWithFileNames(state, [action.payload]),
      )
      .addCase(sparxsEditorSaveSucceededForFiles, (state, action) =>
        filterOutConflictsWithFileNames(state, action.payload.fileNames),
      )
      .addCase(
        sparxsEditorConflictResolvedWithServerBlueprint,
        (state, action) =>
          filterOutConflictsWithFileNames(state, [action.payload.fileName]),
      )
      .addCase(sparxsEditorRevertedSessionFileToOriginal, (state, action) =>
        filterOutConflictsWithFileNames(state, [action.payload.fileName]),
      );
  },
});

export const { setConflicts, clearConflicts, removeConflictFileNames } =
  conflictSlice.actions;

export const selectConflicts = (state: RootState): ConflictEntry[] =>
  state.editor.conflict ?? [];

export const selectConflictFileNames = createSelector(
  [selectConflicts],
  (conflicts) => conflicts.map((c) => c.fileName),
);

export const selectHasConflicts = (state: RootState): boolean =>
  (state.editor.conflict?.length ?? 0) > 0;

export default conflictSlice.reducer;
